import { Link } from 'react-router-dom';
import {
  ArrowIcon, PhoneLink, SERVICES, Seo, SiteFooter, SiteHeader, faqJsonLd, localBusinessJsonLd, useScrollToHash,
} from '../components/shared';
import {
  AreasWeServe, BeforeAfterGallery, Faq, HowItWorks, ReviewPrompt, TrustBar,
} from '../components/OwnerSections';
import { CITIES } from '../data/locations';

const OTHER_CITIES = CITIES.filter((c) => c.slug !== 'toronto').map((c) => c.name);

const FAQ = [
  {
    q: 'How much does it cost?',
    a: "Every job is quoted individually, based on your property and what needs doing. Book a free quote and we'll send you a price for the whole job. No hourly rates and no obligation.",
  },
  {
    q: 'Who are the students?',
    a: 'Local students who have passed our ID check: a photo ID, a selfie and a student card. They bring their own tools.',
  },
  {
    q: 'Can I get my lawn mowed all season?',
    a: 'Yes. Book a weekly or bi-weekly seasonal package, or one-time cuts whenever you need them. The city boulevard strip between the sidewalk and the road is always cut along with your front lawn.',
  },
  {
    q: 'Can I get snow removal for the whole winter?',
    a: "Yes. We quote full-winter contracts that run from the first snowfall to the last, with a student coming out whenever more than 2 inches (about 5 cm) falls. If you'd rather not commit, book a one-time snow removal whenever you need it.",
  },
  {
    q: 'Do I need to be home?',
    a: 'No. Leave the gates unlocked or add access notes when you book. Before and after photos come to you, and the student is paid once you approve the work.',
  },
  {
    q: 'Which areas do you serve?',
    a: `Toronto and the rest of the GTA, including ${OTHER_CITIES.slice(0, -1).join(', ')} and ${OTHER_CITIES.at(-1)}.`,
  },
];

export default function HomeOwnersPage() {
  useScrollToHash();

  return (
    <div className="page">
      <Seo
        title="Lawn Care & Yard Services by Local Students | Student For Hire"
        description="Lawn mowing, weeding, leaf cleanups, snow removal and more across the GTA, done by ID-checked local students. Book a free quote."
        jsonLd={[localBusinessJsonLd(), faqJsonLd(FAQ)]}
      />

      <SiteHeader>
        <a href="#services" className="nav-link nav-link--wide">Services</a>
        <a href="#seasonal-packages" className="nav-link nav-link--wide">Our work</a>
        <Link to="/students" className="nav-link">For students</Link>
        <PhoneLink />
        <Link to="/book" className="btn btn--primary btn--header">Get a quote</Link>
      </SiteHeader>

      <main>
        <section className="hero hero--owners">
          <img className="hero__img" src="/photos/front-lawn-mowing.jpg" alt="A freshly cut front lawn" width="1600" height="1200" loading="eager" fetchPriority="high" />
          <div className="hero__scrim" />
          <div className="hero__inner">
            <div className="wrap">
              <div className="hero__copy">
                <div className="eyebrow">For home owners</div>
                <h1 className="h1">Hand the yard to a student, anywhere in the GTA.</h1>
                <p className="hero__lede">
                  Lawn mowing, weeding, leaf cleanups and snow removal, done by vetted students from your own neighbourhood in
                  Toronto and across the GTA. Tell us what you need and we send you a quote.
                </p>
                <div className="hero__actions">
                  <Link to="/book" className="btn btn--primary btn--lg">Get a free quote <ArrowIcon /></Link>
                  <a href="#seasonal-packages" className="btn btn--ghost">See our work</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TrustBar />

        <section id="services" className="section">
          <div className="wrap">
            <h2 className="h2">Yard work and home help we take care of.</h2>
            <p className="lede">
              Every job is quoted individually — book a quote and we price it around your property, not a stopwatch.
            </p>
            <ul className="service-grid">
              {SERVICES.map(({ key, label, Icon }) => (
                <li key={key} className="service-tile">
                  <Icon size={42} />
                  <span className="service-tile__label">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <BeforeAfterGallery id="seasonal-packages" />

        <HowItWorks />

        <Faq id="faq" title="Questions home owners ask." items={FAQ} white />

        <AreasWeServe />

        <ReviewPrompt white />

        <section id="referrals" className="section section--navy section--center">
          <div className="wrap">
            <h2 className="h2">Book a free quote.</h2>
            <p>Send us the job and we will come back with a price. No hourly rates, no subscription, no obligation.</p>
            <Link to="/book" className="btn btn--primary btn--lg btn--on-navy">Get a free quote <ArrowIcon /></Link>
          </div>
        </section>
      </main>

      <SiteFooter crossLink={{ to: '/students', label: 'For students' }} />
    </div>
  );
}
